import { memo } from "react";
import PropTypes from "prop-types";
import IconBagageSoute from "app/pages/.shared/static/icons/IconBagageSoute";
import get from "lodash/get";
import { FormattedMessage, FormattedPlural } from "react-intl";
import IconBagageCabine from "app/pages/.shared/static/icons/IconBagageCabine";
import { FLIGHT_OPTIONS_STATUS, TRANSPORTATION_TYPES } from "app/constants";
import "./LuggageSummary.scss";
import IconBagageAMain from "app/pages/.shared/IconBagageAMain1";
import LuggagePlaceholderLoader from "app/pages/Booking/Quotation/Luggage/LuggagePlaceholderLoader/LuggagePlaceholderLoader";
import classNames from "classnames";

const LuggageSummary = ({
	flight = {},
	flightOptionsFlightCode,
	flightOptions = {},
	bookingLuggageCount,
	luggageMaxCount,
	transportationType,
	onLuggageClick = () => {},
	hideAddLuggageButton,
	hideIncludedLuggageLabel,
	flightOptionsStatus,
}) => {
	const isTravelByTrain = transportationType === TRANSPORTATION_TYPES.TRAIN;

	const isFlightCodeMatch = flightOptionsFlightCode === flight?.code;

	const luggage = isFlightCodeMatch
		? flightOptions?.flightBagageOption
		: flight?.flightOptions?.flightBagageOption;

	const totalLuggageCount = get(luggage, "quantity");

	let luggageCount = 0;

	if (bookingLuggageCount) {
		luggageCount = bookingLuggageCount;
	} else if (totalLuggageCount) {
		luggageCount = totalLuggageCount;
	} else if (get(luggage, "included")) {
		const luggageMaxCountPerPassenger = Math.floor(totalLuggageCount / luggageMaxCount);
		luggageCount = totalLuggageCount ? luggageMaxCountPerPassenger : 1;
	} else if (get(luggage, "canBeBooked")) {
		luggageCount = 0;
	}

	const isCabinLuggageNotIncluded = get(luggage, "cabinBaggage.included") === false;

	const isBaggageNotBookable =
		get(luggage, "included") === false && get(luggage, "canBeBooked") === false;

	const isLoading = flightOptionsStatus === FLIGHT_OPTIONS_STATUS.LOADING;

	if (isLoading) {
		return (
			<div className="luggage-summary__placeholder-loader">
				<LuggagePlaceholderLoader />
			</div>
		);
	}

	let luggageDisplayValue = "";

	const includedLuggageLabel = !hideIncludedLuggageLabel && (
		<span className="luggage-summary--included">
			: <FormattedMessage id="general.luggage.included" />
		</span>
	);

	if (get(luggage, "included") === true) {
		luggageDisplayValue = <span>{includedLuggageLabel}</span>;
	} else if (isBaggageNotBookable) {
		luggageDisplayValue = (
			<>
				: <FormattedMessage id="quotation.luggage.not.available" />
			</>
		);
	} else if (!hideAddLuggageButton) {
		luggageDisplayValue = (
			<span className="luggage-summary__add" onClick={onLuggageClick}>
				: +{" "}
				<span>
					<FormattedMessage id="quotation.luggage.bookable.label" />
				</span>
			</span>
		);
	}

	let handLuggageCountClassName = classNames("luggage-summary__count", {
		"luggage-summary__count--included": luggageMaxCount > 0,
	});

	let cabinLuggageCountClassName = classNames("luggage-summary__count", {
		"luggage-summary__count--included": !isCabinLuggageNotIncluded,
	});

	let holdLuggageCountClassName = classNames("luggage-summary__count", {
		"luggage-summary__count--included": luggageCount > 0,
	});

	let handLuggageLabelClassName = classNames("luggage-summary__label", {
		"luggage-summary__label--included": luggageMaxCount > 0,
	});

	let cabinLuggageLabelClassName = classNames("luggage-summary__label", {
		"luggage-summary__label--included": !isCabinLuggageNotIncluded,
	});

	let holdLuggageLabelClassName = classNames("luggage-summary__label", {
		"luggage-summary__label--included": luggageCount > 0,
	});

	return (
		<div className="luggage-summary">
			{/* BAGAGE A MAIN */}
			{isCabinLuggageNotIncluded && (
				<div className="luggage-summary--item">
					<span className="luggage-summary__icon">
						<IconBagageAMain width="20px" height="20px" />
					</span>
					<span className={handLuggageLabelClassName} data-testid="luggage-summary-label">
						{luggageMaxCount >= 0 && (
							<span
								className={handLuggageCountClassName}
								data-testid="hand-luggage-summary-count"
							>
								{luggageMaxCount}x{" "}
							</span>
						)}
						<FormattedMessage id="quotation.luggage.handbag.plural.label" />
						{includedLuggageLabel}
					</span>
				</div>
			)}

			{/* BAGAGE EN CABINE */}
			<div className="luggage-summary--item">
				<span className="luggage-summary__icon">
					<IconBagageCabine width="20px" height="20px" />
				</span>
				<span
					className={cabinLuggageLabelClassName}
					data-testid="luggage-summary-cabin-label"
				>
					{luggageMaxCount >= 0 && (
						<span
							className={cabinLuggageCountClassName}
							data-testid="cabin-luggage-summary-count"
						>
							{isCabinLuggageNotIncluded ? 0 : luggageMaxCount}x{" "}
						</span>
					)}
					{isTravelByTrain ? (
						<FormattedMessage id="quotation.transport.luggage.small.plural" />
					) : (
						<FormattedPlural
							value={isCabinLuggageNotIncluded ? 0 : luggageMaxCount}
							one={<FormattedMessage id="quotation.luggage.cabin.singular.label" />}
							other={<FormattedMessage id="quotation.luggage.cabin.plural.label" />}
						/>
					)}{" "}
					{isCabinLuggageNotIncluded ? (
						<>
							: <FormattedMessage id="quotation.luggage.cabin.description.message" />
						</>
					) : (
						includedLuggageLabel
					)}
				</span>
			</div>
			{/* BAGAGE EN SOUTE */}
			<div className="luggage-summary--item">
				<span className="luggage-summary__icon">
					<IconBagageSoute width="20px" height="20px" />
				</span>
				<span className={holdLuggageLabelClassName} data-testid="luggage-summary-label">
					<span
						className={holdLuggageCountClassName}
						data-testid="hold-luggage-summary-count"
					>
						{luggageCount}x{" "}
					</span>{" "}
					{isTravelByTrain ? (
						<FormattedPlural
							value={luggageCount}
							one={<FormattedMessage id="quotation.transport.luggage.regular" />}
							other={
								<FormattedMessage id="quotation.transport.luggage.regular.pluralized" />
							}
						/>
					) : (
						<FormattedPlural
							value={luggageCount}
							one={<FormattedMessage id="quotation.luggage.singular.label" />}
							other={<FormattedMessage id="quotation.luggage.plural.label" />}
						/>
					)}{" "}
					{luggageDisplayValue}
				</span>
			</div>
		</div>
	);
};

LuggageSummary.propTypes = {
	transportationType: PropTypes.oneOf([TRANSPORTATION_TYPES.FLIGHT, TRANSPORTATION_TYPES.TRAIN]),
	onLuggageClick: PropTypes.func,
	flight: PropTypes.object,
	bookingLuggageCount: PropTypes.number,
	luggageMaxCount: PropTypes.number,
	hideIncludedLuggageLabel: PropTypes.bool,
	hideAddLuggageButton: PropTypes.bool,
	flightOptionsFlightCode: PropTypes.string,
	flightOptions: PropTypes.object,
	flightOptionsStatus: PropTypes.oneOf([
		FLIGHT_OPTIONS_STATUS.LOADING,
		FLIGHT_OPTIONS_STATUS.NO_BAGAGE,
		FLIGHT_OPTIONS_STATUS.SUCCESS,
	]),
};

export default memo(LuggageSummary);
